/**
 * jobBriefRules.js — Deterministic, explainable rules for computing the
 * three Job Brief indicators.  We intentionally return integer buckets
 * ("Low / Moderate / High / Critical") rather than any predicted
 * probability of an incident.
 */

/* ── Job Risk buckets ─────────────────────────────────────────────── */
const RISK_LEVELS = ['low', 'moderate', 'high', 'critical'];

function riskFromSignals(signals) {
  // Weight = count of triggered signals; simple + explainable.
  const n = signals.length;
  if (n >= 5) return 'critical';
  if (n >= 3) return 'high';
  if (n >= 1) return 'moderate';
  return 'low';
}

/**
 * computeJobRisk(draft) → { level, signals[] }
 * A single "why did we bucket you here?" list drives the UI explanation.
 */
export function computeJobRisk(draft = {}) {
  const s = [];
  const ji  = draft.job_information || {};
  if (ji.voltage)                            s.push('Voltage present in job info');
  if (ji.equipment_status === 'energized')   s.push('Equipment currently energized');
  if (ji.switching_required === true)        s.push('Switching required');
  if (ji.clearance_required === true)        s.push('Clearance required');
  if (ji.grounding_required === true)        s.push('Grounding required');
  if (ji.lifting_required === true)          s.push('Lifting required');
  if (ji.contractors_present === true)       s.push('Contractors present');
  if ((draft.energy_sources || []).length)   s.push('Energy sources identified');
  if ((draft.error_traps || []).length >= 3) s.push('3+ error traps present');
  if ((draft.critical_steps || []).length)   s.push('Critical steps identified');
  if ((draft.hazards || []).some((h) => h?.category === 'adjacent_energized')) {
    s.push('Adjacent energized equipment');
  }

  return { level: riskFromSignals(s), signals: s };
}

/* ── Required-field map used for Brief Completeness ───────────────── */
const REQUIRED_FIELDS = [
  ['job_information', 'job_title'],
  ['job_information', 'work_location'],
  ['job_information', 'crew_leader'],
  ['job_information', 'date'],
  ['scope',           'in_scope'],
  ['scope',           'out_of_scope'],
  ['scope',           'stop_and_rebrief_if'],
  ['emergency',       'muster_point'],
  ['emergency',       'nearest_medical_facility'],
  ['emergency',       'contact_number'],
];

function _hasVal(v) {
  if (v == null) return false;
  if (Array.isArray(v)) return v.length > 0;
  return String(v).trim().length > 0;
}

/**
 * computeCompleteness(draft) → { pct, missing[] }
 * Percentage of required-field slots that are populated PLUS a plain-
 * English list of what's still missing so the UI can guide the user.
 */
export function computeCompleteness(draft = {}) {
  const missing = [];
  let filled = 0;
  REQUIRED_FIELDS.forEach(([section, field]) => {
    const val = (draft[section] || {})[field];
    if (_hasVal(val)) filled += 1;
    else missing.push(`${section}.${field}`);
  });

  // Extra: at least 1 crew member.
  if ((draft.crew || []).length > 0) filled += 1;
  else missing.push('crew');

  // Extra: at least one hazard reviewed (even if dismissed) OR none applies.
  if ((draft.hazards || []).length > 0) filled += 1;
  else missing.push('hazards.reviewed');

  const total = REQUIRED_FIELDS.length + 2;
  const pct = Math.round((filled / total) * 100);
  return { pct, missing };
}

/**
 * computeHpReadiness(draft) → { pct, signals[] }
 * Percent of recommended HP tools that have been acknowledged/assigned.
 * When no tools have been recommended yet, we return 0% with a helpful
 * signal so the UI can prompt the user to review error traps.
 */
export function computeHpReadiness(draft = {}) {
  const tools = draft.hp_tools || [];
  if (tools.length === 0) {
    return { pct: 0, signals: ['Review error traps to see recommended HP tools.'] };
  }
  const done = tools.filter((t) => t.status === 'acknowledged' || t.status === 'assigned' || t.status === 'completed').length;
  const pct  = Math.round((done / tools.length) * 100);
  const signals = [];
  if (done < tools.length) signals.push(`${tools.length - done} recommended tool(s) still need attention.`);
  return { pct, signals };
}

/**
 * computeReadiness(draft) — bundle used by the ReadinessScores component.
 * Never combines the three numbers into a single "overall score" — that
 * would be misleading.
 */
export function computeReadiness(draft) {
  const risk         = computeJobRisk(draft);
  const completeness = computeCompleteness(draft);
  const hp           = computeHpReadiness(draft);
  return {
    job_risk:               risk.level,
    brief_completeness_pct: completeness.pct,
    hp_readiness_pct:       hp.pct,
    missing_items:          completeness.missing,
    signals:                [...risk.signals, ...hp.signals],
  };
}

export const RISK_ORDER = RISK_LEVELS;
